import { useState } from 'react';
import { Divider, Flex, Modal, List } from 'antd';

import { User, UserReplacement } from '@Types/index';
import { MButton, MInput, MSkeleton } from '@Components/Base';

import InfiniteScroll from 'react-infinite-scroll-component';
import EmployeeItem from './components/EmployeeItem';

import { MAX_LIMIT_SCROLL_LENGTH_USERS } from '../constants';

import styles from './employee-select.module.css';

interface EmployeesProps {
  chosenUser: User | UserReplacement | null;
  users: User[] | UserReplacement[];
  onUserClick: (user: User | UserReplacement) => void;
  onLoadMore?: () => void;
}

const Employees: React.FC<EmployeesProps> = ({
  users,
  onUserClick,
  chosenUser,
  onLoadMore,
}) => {
  return (
    <div id="scrollableReplacementList" className={styles.employees}>
      <InfiniteScroll
        next={() => onLoadMore?.()}
        hasMore={users.length < MAX_LIMIT_SCROLL_LENGTH_USERS}
        loader={<MSkeleton paragraph={{ rows: 1 }} active />}
        dataLength={users.length}
        scrollableTarget="scrollableReplacementList"
      >
        <List
          dataSource={users as User[]}
          itemLayout="vertical"
          renderItem={(user) => (
            <EmployeeItem
              key={user.empId || user.empNumber}
              user={user}
              chosenUser={chosenUser}
              onUserClick={onUserClick}
            />
          )}
        />
      </InfiniteScroll>
    </div>
  );
};

interface ModalContentProps {
  users: User[] | UserReplacement[];
  onSearchChange: React.ChangeEventHandler<HTMLInputElement>;
  onClose: () => void;
  onUserSelect: (user: User | UserReplacement | null) => void;
  onLoadMore: (() => void) | undefined;
}

const ModalContent: React.FC<ModalContentProps> = (props) => {
  const { users, onSearchChange, onClose, onUserSelect, onLoadMore } = props;

  const [user, setUser] = useState<User | UserReplacement | null>(null);

  const onUserClick = (chosenUser: User | UserReplacement): void => {
    setUser(chosenUser);
  };

  const onApply = (): void => {
    onUserSelect(user);
  };

  return (
    <Flex vertical>
      <div className={styles.wrapper}>
        <Divider className={styles.divider} />
        <span className={styles.title}>Search By Employee Name or ID</span>
        <MInput
          searchInput
          onChange={onSearchChange}
          placeholder="Insert Name or ID"
          className={styles.input}
        />
        <Employees
          onLoadMore={onLoadMore}
          chosenUser={user}
          users={users}
          onUserClick={onUserClick}
        />
      </div>
      <Flex justify="space-between" className={styles.buttons}>
        <MButton type="tertiary" onClick={onClose} className={styles.btn}>
          Cancel
        </MButton>
        <MButton type="primary" onClick={onApply} className={styles.btn}>
          Apply
        </MButton>
      </Flex>
    </Flex>
  );
};

interface EmployeeSelectProps {
  isOpen: boolean;
  users: User[] | UserReplacement[];
  title: string;
  onSearchChange: React.ChangeEventHandler<HTMLInputElement>;
  onClose: () => void;
  onUserSelect: (user: User | UserReplacement | null) => void;
  onLoadMore: (() => void) | undefined;
}

const EmployeeSelect: React.FC<EmployeeSelectProps> = (props) => {
  const {
    isOpen,
    users,
    title,
    onSearchChange,
    onClose,
    onUserSelect,
    onLoadMore,
  } = props;

  return (
    <Modal
      title={title}
      open={isOpen}
      footer={null}
      onCancel={onClose}
      width={466}
      className={styles.modal}
      closable={false}
    >
      <ModalContent
        users={users}
        onClose={onClose}
        onSearchChange={onSearchChange}
        onUserSelect={onUserSelect}
        onLoadMore={onLoadMore}
      />
    </Modal>
  );
};

export default EmployeeSelect;
